import MaskedTextField from '@/common/components/MaskedTextField'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { cnpjCpfMask } from '@/common/constants/masks.constant'
import { useBankInstitutions } from '@/common/hooks/queries/useBankInstitutions'
import useDebounce from '@/common/hooks/useDebounce'
import { BankInstitutionsTransformer } from '@/common/services/bankInstitutionsTransformer'
import { Mapper } from '@/common/services/mapper'
import { EstablishmentLeadsFunnelUpdateValidationSchema } from '@/features/establishmentLeadsFunnel/schemas/update.schema'
import { EstablishmentLeadData } from '@/features/establishmentLeadsFunnel/types/establishmentLead.type'
import { BaseSyntheticEvent } from 'react'
import { Control, UseFormRegister, type FieldErrors } from 'react-hook-form'

interface BankAccountInformationSectionProps {
  bank_account_information: EstablishmentLeadData['bank_account_information']
  control: Control<EstablishmentLeadsFunnelUpdateValidationSchema>
  register: UseFormRegister<EstablishmentLeadsFunnelUpdateValidationSchema>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submittableOnBlur?: (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => void
  errors?: FieldErrors<EstablishmentLeadsFunnelUpdateValidationSchema>
  disabled?: boolean
}

export const BankAccountInformationSection = ({
  bank_account_information,
  control,
  register,
  submit,
  submittableOnBlur,
  disabled,
  errors,
}: BankAccountInformationSectionProps) => {
  const [searchBankInstitutions, setSearchBankInstitutions] = useDebounce('')

  const { data: bankInstitutionOptions } = useBankInstitutions({
    filters: { search: searchBankInstitutions },
    mapper: Mapper.mapWithTransformer({ transformer: BankInstitutionsTransformer.optionsTransformer() }),
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-[18px] font-bold">Detalhes bancários</p>
      </div>

      <SelectController
        label="Banco"
        name="bank_account_information.bank_institution"
        control={control}
        options={bankInstitutionOptions}
        markAsRequired
        triggerOnChange={submit}
        onInputChange={(value) => setSearchBankInstitutions(value)}
        isDisabled={disabled}
        errorMessage={errors?.bank_account_information?.bank_institution?.message}
      />

      <div className="flex gap-2">
        <TextField
          label="Agência (com dígito)"
          markAsRequired
          className="grow"
          defaultValue={bank_account_information?.agency}
          errorMessage={errors?.bank_account_information?.agency?.message || undefined}
          {...register('bank_account_information.agency')}
          onBlur={(e) => submittableOnBlur?.(e, register('bank_account_information.agency').onBlur)}
          disabled={disabled}
        />

        <TextField
          label="Conta (com dígito)"
          markAsRequired
          className="grow"
          defaultValue={bank_account_information?.account}
          errorMessage={errors?.bank_account_information?.account?.message || undefined}
          {...register('bank_account_information.account')}
          onBlur={(e) => submittableOnBlur?.(e, register('bank_account_information.account').onBlur)}
          disabled={disabled}
        />
      </div>

      <div className='flex gap-2'>
        <TextField
          className='w-1/2'
          label="Nome do favorecido"
          markAsRequired
          defaultValue={bank_account_information?.payee_name}
          errorMessage={errors?.bank_account_information?.payee_name?.message || undefined}
          {...register('bank_account_information.payee_name')}
          onBlur={(e) => submittableOnBlur?.(e, register('bank_account_information.payee_name').onBlur)}
          disabled={disabled}
        />

        <MaskedTextField
          className='w-1/2'
          label="CNPJ/CPF do favorecido"
          mask={cnpjCpfMask}
          defaultValue={bank_account_information?.payee_cnpj}
          errorMessage={errors?.bank_account_information?.payee_cnpj?.message || undefined}
          {...register('bank_account_information.payee_cnpj')}
          onBlur={(e) => submittableOnBlur?.(e, register('bank_account_information.payee_cnpj').onBlur)}
          disabled={disabled}
        />
      </div>
    </div>
  )
}
